import React, { useState, useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { FiChevronRight ,FiSend} from 'react-icons/fi';
import { IoSparkles } from 'react-icons/io5';

const Breadcrumb = () => (
  <div className="bg-white flex items-center gap-2 text-sm text-gray-500">
    <a href="/dashboard" className="hover:underline">Candidates</a>
    <FiChevronRight />
    <span className="font-semibold text-emerald-600">Enhance Job Description</span>
  </div>
);

const WS_URL = process.env.REACT_APP_CHAT_WS_URL || 'ws://localhost:8000/ws/chat/ai/';

const CreateJobPage = ({
  initialDescription = '',
  onSave,
  onCancel,
}) => {
  const [messages, setMessages] = useState([
    { role: 'system', content: initialDescription }
  ]);
  const [input, setInput] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedMessage, setStreamedMessage] = useState('');
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);

  // Open websocket connection on mount, close on unmount
  useEffect(() => {
    let ws;
    let isUnmounted = false;

    function openWebSocket() {
      ws = new window.WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isUnmounted) return;
        setIsConnected(true);
        ws.send(JSON.stringify({
          type: 'system',
          content: initialDescription
        }));
      };

      ws.onclose = () => {
        setIsConnected(false);
      };

      ws.onerror = () => {
        setIsConnected(false);
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          data = { type: 'text', content: event.data };
        }
        if (data.type === 'stream') {
          setIsStreaming(true);
          setStreamedMessage((prev) => prev + (data.content || ''));
        } else if (data.type === 'stream_end') {
          setIsStreaming(false);
          setMessages((prev) => [
            ...prev,
            { role: 'ai', content: streamedMessage + (data.content || '') }
          ]);
          setStreamedMessage('');
        } else if (data.type === 'text' || data.type === 'ai') {
          setMessages((prev) => [
            ...prev,
            { role: 'ai', content: data.content }
          ]);
        }
      };
    }

    openWebSocket();

    return () => {
      isUnmounted = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [initialDescription]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamedMessage]);

  const handleSend = useCallback((e) => {
    e.preventDefault();
    if (!input.trim() || !isConnected) return;
    const userMsg = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMsg]);
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({
        type: 'user',
        content: input
      }));
    }
    setInput('');
  }, [input, isConnected]);

  const handleSave = () => {
    // Save the latest AI message as the enhanced description
    const lastAI = [...messages].reverse().find(m => m.role === 'ai');
    if (onSave) onSave(lastAI ? lastAI.content : initialDescription);
  };

  // Parse and sanitize the markdown job description
  const parsedDescription = initialDescription
    ? DOMPurify.sanitize(marked.parse(initialDescription))
    : '';

  return (
    <div className="mt-8 h-[calc(100vh-4rem)] overflow-auto">
      <header className="text-center flex items-center justify-center gap-3 mb-4">
        <IoSparkles className="h-5 w-5 text-emerald-500" />
        <div className="text-xl font-semibold mph-primary">
          Enhance your job description with AI
        </div>
      </header>

      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-6">
        <div className="mb-4">
          {/* <div className="text-xs text-gray-500 mb-1">Current Job Description</div> */}
          <div className=" border rounded p-3 text-xs text-gray-700 prose prose-green max-w-none">
            {initialDescription
              ? <div dangerouslySetInnerHTML={{ __html: parsedDescription }} />
              : <span className="italic text-gray-400">No description provided.</span>
            }
          </div>
        </div>

        <div className="mb-4 hidden">
          <div className="text-xs text-gray-500 mb-1">AI Chat</div>
          <div className="h-64 overflow-y-auto bg-gray-50 border rounded p-3 text-xs flex flex-col gap-2">
            {messages
              .filter(m => m.role !== 'system')
              .map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`px-3 py-2 rounded-lg max-w-[80%] ${msg.role === 'user'
                        ? 'bg-emerald-100 text-emerald-900'
                        : 'bg-white border text-gray-700'
                      }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
            {isStreaming && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg bg-white border text-gray-700 max-w-[80%]">
                  {streamedMessage}
                  <span className="animate-pulse">|</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </div>
<div className="sticky bottom-0 bg-white py-3 border-t">
  <form className="flex items-end gap-2 mt-2" onSubmit={handleSend}>
    <textarea
  value={input}
  onChange={(e) => {
    setInput(e.target.value);
    e.target.style.height = "auto"; // reset height
    e.target.style.height = e.target.scrollHeight + "px"; // grow dynamically
  }}
  placeholder="Ask AI to improve, clarify, or rewrite the description..."
  className="flex-1 border rounded-[5px] px-4 py-2 text-xs outline-none resize-none overflow-y-auto"
  style={{
    maxHeight: `${1.5 * 5}em`, // roughly 5 lines
    lineHeight: "1.5em",
  }}
  rows={1}
  disabled={false}
/>

          <button
            className="rounded-full flex items-center text-xs gap-2 bg-gradient text-white font-semibold px-3 py-2  hover:shadow-lg transition-shadow disabled:opacity-50"
            disabled={!input.trim() || isStreaming}
          >
            <FiSend className='font-lg' />
          </button>
        </form>

        <div className="flex justify-end gap-2 mt-6">
          <button
            className="px-4 py-1 rounded rounded-2xl bg-gray-200 text-gray-333 text-xs"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-full bg-gradient text-white text-xs font-semibold hover:shadow-lg"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJobPage;